import { ReactNode, useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useSidebar } from '../contexts/SidebarContext'
import { useTaskPanel } from '../contexts/TaskPanelContext'
import { useChatPanel } from '../contexts/ChatPanelContext'
import { useGeminiPanel } from '../contexts/GeminiPanelContext'
import { useGeminiContext } from '../contexts/GeminiContextContext'
import { TaskPanel } from './TaskPanel'
import { ChatPanel } from './ChatPanel'
import { LetterWritingPanel } from './LetterWritingPanel'
import { GeminiPanel } from './GeminiPanel'
import { ChatNotificationBadge } from './ChatNotificationBadge'
import { TaskNotificationBadge } from './TaskNotificationBadge'
import { GuidelinesFlyout } from './GuidelinesFlyout'
import { tenantSettingsService } from '../services/tenantSettingsService'
import { useTenantProductType } from '../hooks/useTenantProductType'
import { useLeaveModuleEnabled } from '../modules/leave/useLeaveModuleEnabled'
import { isAdminDomain } from '../utils/tenant'
import '../styles/navigation-tokens.css'
import './Layout.css'
import appPackage from '../../package.json'

// Default session timeout configuration (milliseconds)
// Warning dialog will appear SESSION_WARNING_BEFORE_MS before automatic logout.
const DEFAULT_SESSION_TIMEOUT_MINUTES = 30
const SESSION_WARNING_BEFORE_MS = 1 * 60 * 1000 // Show warning 1 minute before logout

interface LayoutProps {
  children: ReactNode
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user, logout, hasPermission } = useAuth()
  const { sidebarVisible, toggleSidebar, sidebarCollapsed, toggleCollapse } = useSidebar()
  const { togglePanel: toggleTaskPanel } = useTaskPanel()
  const { togglePanel: toggleChatPanel } = useChatPanel()
  const { togglePanel: toggleGeminiPanel, isOpen: isGeminiPanelOpen } = useGeminiPanel()
  const { consultationContext } = useGeminiContext()

  // Check if suggestions are available (context exists and has content)
  const hasSuggestionsAvailable = consultationContext !== null && (
    consultationContext.notes ||
    consultationContext.reasonForVisit ||
    consultationContext.currentMedications?.length ||
    consultationContext.allergies?.length ||
    consultationContext.chronicConditions?.length ||
    consultationContext.medicalHistory
  )

  // Add animation class when suggestions are available but panel is closed
  const shouldAnimateFAB = hasSuggestionsAvailable && !isGeminiPanelOpen

  const isActive = (path: string) => location.pathname === path
  const [showSessionWarning, setShowSessionWarning] = useState(false)
  const [sessionCountdownSeconds, setSessionCountdownSeconds] = useState<number | null>(null)
  const [guidelinesFlyoutOpen, setGuidelinesFlyoutOpen] = useState(false)
  const guidelinesFabRef = useRef<HTMLButtonElement>(null)
  
  // Use ref to track current warning state for event listeners
  const showSessionWarningRef = useRef(false)
  useEffect(() => {
    showSessionWarningRef.current = showSessionWarning
  }, [showSessionWarning])

  // Load tenant-specific settings (including session timeout)
  const { data: tenantSettings, isLoading: isLoadingSettings, error: settingsError } = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: async () => {
      try {
        const result = await tenantSettingsService.getCurrent()
        return result
      } catch (error) {
        console.error('[Session Timeout] Error fetching settings:', error)
        throw error
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  // Allow testing with shorter timeout via localStorage (for debugging)
  // To test: localStorage.setItem('clnicl_test_session_timeout_minutes', '1') // 1 minute
  // To reset: localStorage.removeItem('clnicl_test_session_timeout_minutes')
  const testTimeoutMinutes = typeof window !== 'undefined' 
    ? Number(localStorage.getItem('clnicl_test_session_timeout_minutes') || '0')
    : 0
  const effectiveTimeoutMinutes = testTimeoutMinutes > 0 
    ? testTimeoutMinutes 
    : (tenantSettings?.sessionTimeoutMinutes ?? DEFAULT_SESSION_TIMEOUT_MINUTES)
  
  const sessionTimeoutMs = effectiveTimeoutMinutes * 60 * 1000
  const logoUrl = (tenantSettings?.logoUrl ?? '').trim() || '/logo@0.5x.png'
  const appVersion = appPackage.version ?? '0.0.0'
  const { isPptOnly } = useTenantProductType()
  const { enabled: leaveModuleEnabled } = useLeaveModuleEnabled()
  const copyrightYear = new Date().getFullYear()

  // Debug logging removed for production


  // Inactivity-based session timeout with warning dialog
  useEffect(() => {
    // Only run in browser and when user is authenticated
    if (typeof window === 'undefined' || !user) {
      return
    }

    // Don't set up timers until settings are loaded (or failed to load)
    // But if there's an error, use default timeout
    if (isLoadingSettings) {
      return
    }

    let warningTimeoutId: number | undefined
    let logoutTimeoutId: number | undefined
    let countdownIntervalId: number | undefined

    const clearAllTimers = () => {
      if (warningTimeoutId !== undefined) {
        window.clearTimeout(warningTimeoutId)
        warningTimeoutId = undefined
      }
      if (logoutTimeoutId !== undefined) {
        window.clearTimeout(logoutTimeoutId)
        logoutTimeoutId = undefined
      }
      if (countdownIntervalId !== undefined) {
        window.clearInterval(countdownIntervalId)
        countdownIntervalId = undefined
      }
    }

    const startCountdown = () => {
      const totalSeconds = SESSION_WARNING_BEFORE_MS / 1000
      setSessionCountdownSeconds(totalSeconds)

      countdownIntervalId = window.setInterval(() => {
        setSessionCountdownSeconds((prev) => {
          if (prev === null || prev <= 1) {
            if (countdownIntervalId !== undefined) {
              window.clearInterval(countdownIntervalId)
              countdownIntervalId = undefined
            }
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }

    const scheduleTimeouts = () => {
      clearAllTimers()

      const warningDelay = Math.max(sessionTimeoutMs - SESSION_WARNING_BEFORE_MS, 0)

      // Warn shortly before logout
      warningTimeoutId = window.setTimeout(() => {
        setShowSessionWarning(true)
        startCountdown()
      }, warningDelay)

      // Actual logout due to inactivity
      logoutTimeoutId = window.setTimeout(async () => {
        setShowSessionWarning(false)
        setSessionCountdownSeconds(null)
        clearAllTimers()
        // Use existing logout + navigation behaviour
        await handleLogout()
      }, sessionTimeoutMs)
    }

    const activityEvents: Array<keyof WindowEventMap> = [
      'click',
      'keydown',
      'mousemove',
      'scroll',
      'touchstart',
    ]

    const handleActivity = (_eventType?: string) => {
      // Use ref to get current value (avoids stale closure)
      const isWarningVisible = showSessionWarningRef.current
      
      // Reset timers on any user activity
      if (!isWarningVisible) {
        scheduleTimeouts()
      } else {
        // If warning is visible and user interacts, keep them logged in
        setShowSessionWarning(false)
        setSessionCountdownSeconds(null)
        scheduleTimeouts()
      }
    }

    // Store handlers for cleanup
    const handlers = new Map<string, (e: Event) => void>()
    
    // Attach event listeners with event type tracking
    activityEvents.forEach((eventName) => {
      const wrappedHandler = () => {
        handleActivity(eventName)
      }
      handlers.set(eventName, wrappedHandler)
      window.addEventListener(eventName, wrappedHandler, { passive: true })
    })

    // Initialise timers when layout mounts or when timeout settings change
    scheduleTimeouts()

    return () => {
      activityEvents.forEach((eventName) => {
        const handler = handlers.get(eventName)
        if (handler) {
          window.removeEventListener(eventName, handler)
        }
      })
      handlers.clear()
      clearAllTimers()
    }
    // We intentionally do not include handleLogout in dependencies to avoid
    // re-registering listeners on every render; it is stable for this component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionTimeoutMs, user, isLoadingSettings, settingsError])

  const handleLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }


  return (
    <div className={`layout ${sidebarVisible ? 'sidebar-visible' : 'sidebar-hidden'}`}>
      <a href="#main-content" className="skip-link">
        {t('accessibility.skipToContent')}
      </a>
      <button
        className="sidebar-toggle"
        onClick={toggleSidebar}
        aria-label={sidebarVisible ? 'Hide sidebar' : 'Show sidebar'}
        aria-expanded={sidebarVisible}
        title={sidebarVisible ? 'Hide menu' : 'Show menu'}
      >
        {sidebarVisible ? (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M15 5L5 15M5 5L15 15"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M3 5H17M3 10H17M3 15H17"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
      <header className="top-header">
        <div className="top-header-content">
          <div className="top-header-logo">
            <Link to="/dashboard" aria-label="Go to dashboard">
              <img src={logoUrl} alt="Tenant logo" />
            </Link>
          </div>
          {user && (
            <div className="user-info-header">
              <div className="user-details-header">
                <div className="user-name-header">
                  {user.firstName} {user.lastName}
                </div>
                <div className="user-role-header">{user.role}</div>
              </div>
              <button
                className="btn-logout-header"
                onClick={handleLogout}
                aria-label="Logout"
                title="Logout"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M7.5 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H7.5M13.3333 14.1667L17.5 10M17.5 10L13.3333 5.83333M17.5 10H7.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </header>
      <div className="layout-body">
        <nav
          className={`sidebar ${sidebarVisible ? 'visible' : 'hidden'} ${sidebarCollapsed ? 'collapsed' : ''}`}
          role="navigation"
          aria-label="Main navigation"
        >
          <ul className="nav-menu" role="menubar">
          <li role="none">
            <Link
              to="/dashboard"
              className={isActive('/dashboard') ? 'active' : ''}
              role="menuitem"
              aria-current={isActive('/dashboard') ? 'page' : undefined}
              data-tooltip={sidebarCollapsed ? t('navigation.dashboard') : undefined}
            >
              <span className="nav-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="7" height="7"></rect>
                  <rect x="14" y="3" width="7" height="7"></rect>
                  <rect x="14" y="14" width="7" height="7"></rect>
                  <rect x="3" y="14" width="7" height="7"></rect>
                </svg>
              </span>
              <span className="nav-label">{t('navigation.dashboard')}</span>
            </Link>
          </li>
          {!isPptOnly && (
          <li role="none">
            <Link
              to="/patients"
              className={isActive('/patients') ? 'active' : ''}
              role="menuitem"
              aria-current={isActive('/patients') ? 'page' : undefined}
              data-tooltip={sidebarCollapsed ? t('navigation.patients') : undefined}
            >
              <span className="nav-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </span>
              <span className="nav-label">{t('navigation.patients')}</span>
            </Link>
          </li>
          )}
          {!isPptOnly && (
          <li role="none">
            <Link
              to="/families"
              className={location.pathname.startsWith('/families') ? 'active' : ''}
              role="menuitem"
              aria-current={location.pathname.startsWith('/families') ? 'page' : undefined}
              data-tooltip={sidebarCollapsed ? 'Families' : undefined}
            >
              <span className="nav-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  <path d="M13 7a3 3 0 1 0-6 0 3 3 0 0 0 6 0z"></path>
                </svg>
              </span>
              <span className="nav-label">Families</span>
            </Link>
          </li>
          )}
          {!isPptOnly && (
          <li role="none">
            <Link
              to="/appointments"
              className={isActive('/appointments') ? 'active' : ''}
              role="menuitem"
              aria-current={isActive('/appointments') ? 'page' : undefined}
              data-tooltip={sidebarCollapsed ? t('navigation.appointments') : undefined}
            >
              <span className="nav-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              </span>
              <span className="nav-label">{t('navigation.appointments')}</span>
            </Link>
          </li>
          )}
          {!isPptOnly && hasPermission('billing-accounts:read') && (
            <li role="none">
              <Link
                to="/billing"
                className={isActive('/billing') ? 'active' : ''}
                role="menuitem"
                aria-current={isActive('/billing') ? 'page' : undefined}
                data-tooltip={sidebarCollapsed ? 'Billing' : undefined}
              >
                <span className="nav-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="4" width="18" height="16" rx="2" ry="2"></rect>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                    <line x1="8" y1="15" x2="10" y2="15"></line>
                    <line x1="12" y1="15" x2="16" y2="15"></line>
                  </svg>
                </span>
                <span className="nav-label">Billing</span>
              </Link>
            </li>
          )}
          {!isPptOnly && (
          <li role="none">
            <Link
              to="/lab-results"
              className={location.pathname.startsWith('/lab-results') ? 'active' : ''}
              role="menuitem"
              aria-current={location.pathname.startsWith('/lab-results') ? 'page' : undefined}
              data-tooltip={sidebarCollapsed ? 'Lab Results' : undefined}
            >
              <span className="nav-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="6" x2="12" y2="12"></line>
                  <line x1="16" y1="10" x2="12" y2="12"></line>
                </svg>
              </span>
              <span className="nav-label">Lab Results</span>
            </Link>
          </li>
          )}
          {!isPptOnly && (
          <li role="none">
            <Link
              to="/batch-documents"
              className={location.pathname.startsWith('/batch-documents') ? 'active' : ''}
              role="menuitem"
              aria-current={location.pathname.startsWith('/batch-documents') ? 'page' : undefined}
              data-tooltip={sidebarCollapsed ? 'Batching' : undefined}
            >
              <span className="nav-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                  <path d="M2 17l10 5 10-5"></path>
                </svg>
              </span>
              <span className="nav-label">Batching</span>
            </Link>
          </li>
          )}
          {!isPptOnly && hasPermission('protocols:read') && (
            <li role="none">
              <Link
                to="/protocols"
                className={location.pathname.startsWith('/protocols') ? 'active' : ''}
                role="menuitem"
                aria-current={location.pathname.startsWith('/protocols') ? 'page' : undefined}
                data-tooltip={sidebarCollapsed ? 'Protocols' : undefined}
              >
                <span className="nav-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <path d="M9 12l2 2 4-4"></path>
                  </svg>
                </span>
                <span className="nav-label">Protocols</span>
              </Link>
            </li>
          )}
          {!isPptOnly && hasPermission('research-studies:read') && (
            <li role="none">
              <Link
                to="/research-studies"
                className={location.pathname.startsWith('/research-studies') ? 'active' : ''}
                role="menuitem"
                aria-current={location.pathname.startsWith('/research-studies') ? 'page' : undefined}
                data-tooltip={sidebarCollapsed ? 'Research Studies' : undefined}
              >
                <span className="nav-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 2v20M5 6h8M5 10h8M5 14h8M5 18h8M15 2v20M19 6h-8M19 10h-8M19 14h-8M19 18h-8"></path>
                  </svg>
                </span>
                <span className="nav-label">Research Studies</span>
              </Link>
            </li>
          )}
          {!isPptOnly && (
          <li role="none">
            <Link
              to="/templates"
              className={location.pathname.startsWith('/templates') ? 'active' : ''}
              role="menuitem"
              aria-current={location.pathname.startsWith('/templates') ? 'page' : undefined}
              data-tooltip={sidebarCollapsed ? 'Templates' : undefined}
            >
              <span className="nav-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
              </span>
              <span className="nav-label">Templates</span>
            </Link>
          </li>
          )}
          {hasPermission('patients:read') && (
            <li role="none">
              <Link
                to="/reports"
                className={location.pathname.startsWith('/reports') ? 'active' : ''}
                role="menuitem"
                aria-current={location.pathname.startsWith('/reports') ? 'page' : undefined}
                data-tooltip={sidebarCollapsed ? 'Reports' : undefined}
              >
                <span className="nav-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                    <line x1="18" y1="20" x2="18" y2="10"></line>
                    <line x1="12" y1="20" x2="12" y2="4"></line>
                    <line x1="6" y1="20" x2="6" y2="14"></line>
                  </svg>
                </span>
                <span className="nav-label">Reports</span>
              </Link>
            </li>
          )}
          {leaveModuleEnabled && (
            <li role="none">
              <Link
                to="/team"
                className={location.pathname.startsWith('/team') ? 'active' : ''}
                role="menuitem"
                aria-current={location.pathname.startsWith('/team') ? 'page' : undefined}
                data-tooltip={sidebarCollapsed ? 'Team' : undefined}
              >
                <span className="nav-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </span>
                <span className="nav-label">Team</span>
              </Link>
            </li>
          )}
          {isPptOnly && hasPermission('patients:read') && (
            <li role="none">
              <Link
                to="/stc-signing"
                className={location.pathname.startsWith('/stc-signing') ? 'active' : ''}
                role="menuitem"
                aria-current={location.pathname.startsWith('/stc-signing') ? 'page' : undefined}
                data-tooltip={sidebarCollapsed ? 'New STC/MICS' : undefined}
              >
                <span className="nav-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5v14"></path>
                    <path d="M5 12h14"></path>
                  </svg>
                </span>
                <span className="nav-label">New STC/MICS</span>
              </Link>
            </li>
          )}
          {!isPptOnly && hasPermission('audit:read') && (
            <li role="none">
              <Link
                to="/audit"
                className={location.pathname.startsWith('/audit') ? 'active' : ''}
                role="menuitem"
                aria-current={location.pathname.startsWith('/audit') ? 'page' : undefined}
                data-tooltip={sidebarCollapsed ? 'Audit' : undefined}
              >
                <span className="nav-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                    <path d="M9 12l2 2 4-4"></path>
                  </svg>
                </span>
                <span className="nav-label">Audit</span>
              </Link>
            </li>
          )}
          {isAdminDomain() && (
            <li role="none">
              <Link
                to="/admin/tenants"
                className={location.pathname.startsWith('/admin/tenants') ? 'active' : ''}
                role="menuitem"
                aria-current={location.pathname.startsWith('/admin/tenants') ? 'page' : undefined}
                data-tooltip={sidebarCollapsed ? 'Tenants' : undefined}
              >
                <span className="nav-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                </span>
                <span className="nav-label">Tenants</span>
              </Link>
            </li>
          )}
          <li role="none">
            <Link
              to="/settings"
              className={location.pathname.startsWith('/settings') ? 'active' : ''}
              role="menuitem"
              aria-current={location.pathname.startsWith('/settings') ? 'page' : undefined}
              data-tooltip={sidebarCollapsed ? t('navigation.settings') : undefined}
            >
              <span className="nav-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="3"></circle>
                  <path d="M12 1v6m0 6v6m9-9h-6m-6 0H3"></path>
                </svg>
              </span>
              <span className="nav-label">{t('navigation.settings')}</span>
            </Link>
          </li>
            <li role="none" className="nav-version-item">
              <div
                className="nav-version"
                aria-label={`Version ${appVersion}`}
                data-tooltip={sidebarCollapsed ? `v${appVersion}` : undefined}
              >
                <span className="nav-label">v{appVersion}</span>
              </div>
            </li>
        </ul>
          <div className="sidebar-footer">
            <div className="sidebar-footer-actions">
              <button
                className="sidebar-collapse-toggle"
                onClick={toggleCollapse}
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                title={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
              >
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {sidebarCollapsed ? (
                    <path
                      d="M7.5 5L12.5 10L7.5 15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  ) : (
                    <path
                      d="M12.5 5L7.5 10L12.5 15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  )}
                </svg>
              </button>
              <button
                className="sidebar-close"
                onClick={toggleSidebar}
                aria-label="Close sidebar"
                title="Close menu"
              >
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M15 5L5 15M5 5L15 15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </nav>
        <div className="main-wrapper">
          <main id="main-content" className="main-content" role="main">
            {children}
          </main>
          <footer className="page-footer">
            <span>Copyright (c) {copyrightYear} Clnicl</span>
            <span className="page-footer-separator" aria-hidden="true">|</span>
            <a href="/terms-and-conditions">Terms</a>
            <span className="page-footer-separator" aria-hidden="true">|</span>
            <a href="/privacy-notice">Privacy</a>
            <span className="page-footer-separator" aria-hidden="true">|</span>
            <a href="/pcrs-responsibility">PCRS Statement</a>
          </footer>
        </div>
      </div>
      <button
        className="task-panel-toggle"
        onClick={toggleTaskPanel}
        aria-label="Open task panel"
        title="Tasks"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2.5 5H17.5M2.5 10H17.5M2.5 15H12.5M15 12.5L17.5 15L15 17.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <TaskNotificationBadge />
      </button>
      <button
        className="chat-panel-toggle"
        onClick={toggleChatPanel}
        aria-label="Open chat panel"
        title="Team Chat"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M17.5 5.83333C17.5 6.27536 17.3244 6.69928 17.0118 7.01184C16.6993 7.32441 16.2754 7.5 15.8333 7.5H5.83333L2.5 10.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H15.8333C16.2754 2.5 16.6993 2.67559 17.0118 2.98816C17.3244 3.30072 17.5 3.72464 17.5 4.16667V5.83333Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M17.5 10.8333V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H7.5L4.16667 14.1667V10.8333"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <ChatNotificationBadge />
      </button>
      <button
        ref={guidelinesFabRef}
        className="guidelines-fab-toggle"
        onClick={() => setGuidelinesFlyoutOpen(!guidelinesFlyoutOpen)}
        aria-label="Open clinical guidelines"
        title="Clinical Guidelines"
        aria-expanded={guidelinesFlyoutOpen}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M3.33333 5.83333C3.33333 4.91286 4.07952 4.16667 5 4.16667H15C15.9205 4.16667 16.6667 4.91286 16.6667 5.83333V14.1667C16.6667 15.0871 15.9205 15.8333 15 15.8333H5C4.07952 15.8333 3.33333 15.0871 3.33333 14.1667V5.83333Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M6.66667 8.33333H13.3333M6.66667 11.6667H10"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <GuidelinesFlyout
        isOpen={guidelinesFlyoutOpen}
        onClose={() => setGuidelinesFlyoutOpen(false)}
        buttonRef={guidelinesFabRef}
      />
      {!isPptOnly && (
        <button
          className={`gemini-panel-toggle ${shouldAnimateFAB ? 'has-suggestions' : ''}`}
          onClick={toggleGeminiPanel}
          aria-label="Open AI suggestions panel"
          title="AI Suggestions"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10 2L12.09 7.26L18 8.27L14 12.14L14.91 18.02L10 15.77L5.09 18.02L6 12.14L2 8.27L7.91 7.26L10 2Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
      <TaskPanel />
      <ChatPanel />
      <LetterWritingPanel />
      <GeminiPanel />
      {showSessionWarning && (
        <div className="session-timeout-overlay" role="dialog" aria-modal="true" aria-labelledby="session-timeout-title">
          <div className="session-timeout-dialog">
            <h2 id="session-timeout-title">You&apos;re about to be signed out</h2>
            <p>
              You have been inactive for a while. For security, you will be logged out due to inactivity
              {sessionCountdownSeconds !== null && sessionCountdownSeconds > 0
                ? ` in ${sessionCountdownSeconds} second${sessionCountdownSeconds === 1 ? '' : 's'}.`
                : '.'}
            </p>
            <div className="session-timeout-actions">
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  // Treat this as user activity: dismiss dialog and reset timers
                  setShowSessionWarning(false)
                  setSessionCountdownSeconds(null)
                }}
              >
                Stay signed in
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setShowSessionWarning(false)
                  setSessionCountdownSeconds(null)
                  handleLogout()
                }}
              >
                Sign out now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

